import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Search, Download, Eye, Globe, ShieldCheck, X } from "lucide-react";

/** Cross-org template library — browse/search templates other companies shared,
 * preview their questions, and import an editable copy. */
export function TemplateLibraryDialog({ open, onClose, onImported }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async (query = q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/form_templates/library", { params: query.trim() ? { q: query.trim() } : {} });
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setQ("");
    setPreview(null);
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const doImport = async (t) => {
    setBusyId(t.id);
    try {
      const { data } = await api.post(`/form_templates/library/${t.id}/import`);
      toast.success(`"${data.name}" imported — it's inactive until you review & publish it`);
      onImported?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusyId(null);
    }
  };

  const openPreview = async (t) => {
    try {
      const { data } = await api.get(`/form_templates/library/${t.id}`);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="template-library-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#FF5F15]" /> Template library
          </DialogTitle>
          <DialogDescription className="text-xs">
            Forms shared by other companies on Fieldlink. Import one and edit it to suit your crew.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search templates — e.g. permit, excavation, toolbox…"
            className="pl-9 rounded-sm border-zinc-400"
            data-testid="library-search-input"
          />
        </div>

        {preview ? (
          <div className="border-2 border-zinc-900 p-4" data-testid="library-preview">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display font-bold uppercase tracking-tight text-lg">{preview.name}</div>
                <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                  by {preview.org_name} · {preview.fields.length} questions
                  {preview.category === "safety" && <span className="text-[#FF5F15] font-bold ml-2">SAFETY</span>}
                </div>
                {preview.description && <div className="text-xs text-zinc-600 mt-1">{preview.description}</div>}
              </div>
              <button onClick={() => setPreview(null)} className="text-zinc-400 hover:text-black shrink-0"
                      data-testid="library-preview-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <ol className="mt-3 space-y-1.5 text-sm list-decimal list-inside">
              {preview.fields.map((f) => (
                <li key={f.id} className="text-zinc-700">
                  {f.label} <span className="text-[10px] font-mono uppercase text-zinc-400">· {f.type}{f.required ? " · required" : ""}</span>
                </li>
              ))}
            </ol>
            {!preview.is_mine && (
              <Button onClick={() => doImport(preview)} disabled={busyId === preview.id}
                      className="mt-4 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
                      data-testid="library-preview-import">
                <Download className="w-3.5 h-3.5 mr-1.5" />
                {busyId === preview.id ? "Importing…" : "Import a copy"}
              </Button>
            )}
          </div>
        ) : loading ? (
          <div className="text-sm text-zinc-500 font-mono py-8 text-center">Searching…</div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500" data-testid="library-empty">
            {q.trim() ? "No shared templates match your search." : "No companies have shared templates yet. Share one of yours to start the library!"}
          </div>
        ) : (
          <div className="space-y-2" data-testid="library-list">
            {items.map((t) => (
              <div key={t.id} className="border border-zinc-300 p-3 flex flex-wrap items-center gap-3 hover:border-[#FF5F15] transition-colors"
                   data-testid={`library-item-${t.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-bold text-sm flex items-center gap-2">
                    {t.name}
                    {t.category === "safety" && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest text-[#FF5F15] border border-[#FF5F15] px-1 py-0.5">
                        <ShieldCheck className="w-2.5 h-2.5" /> Safety
                      </span>
                    )}
                    {t.is_mine && (
                      <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 border border-zinc-300 px-1 py-0.5">Yours</span>
                    )}
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                    by {t.org_name} · {t.field_count} questions · imported {t.import_count}×
                  </div>
                  {t.description && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{t.description}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openPreview(t)}
                          className="rounded-sm text-xs font-bold uppercase tracking-wider border-zinc-400"
                          data-testid={`library-preview-${t.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                  </Button>
                  {!t.is_mine && (
                    <Button size="sm" onClick={() => doImport(t)} disabled={busyId === t.id}
                            className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider"
                            data-testid={`library-import-${t.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1" />
                      {busyId === t.id ? "…" : "Import"}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
